// src/pages/About.jsx
import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import {
  collection, query, orderBy, getDocs, getDoc, doc,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import {
  ref, uploadBytesResumable, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const SECTION_ORDER = ['mission', 'history', 'team', 'awards'];
const SCROLL_OFFSET = 120;

const AWARD_CATEGORIES = [
  'Chapter', 'International', 'National', 'Special Recognition', 'Other',
];

const TEAM_ROLES = [
  'President', 'Vice President', 'Secretary', 'Treasurer', 'Auditor',
  'Board Member', 'Committee Chair', 'Committee Member',
  'Web Administrator', 'Adviser', 'Other',
];

const TEAM_COMMITTEES = [
  'Executive Committee', 'Board of Directors', 'Membership Committee',
  'Events Committee', 'Technical Committee',
  'Professional Development Committee', 'Public Relations Committee',
  'Finance Committee', 'Web & IT Committee', 'Awards Committee',
  'Ad Hoc Committee',
];

/* ================================================================== */
/* Animation variants                                                  */
/* ================================================================== */

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const timelineItemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, delay: custom * 0.06, ease: 'easeOut' },
  }),
};

/* ================================================================== */
/* Small utility hook                                                  */
/* ================================================================== */

const useDebounced = (value, delay = 200) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg = type === 'error' ? 'bg-red-500' : 'bg-green-500';
  const icon = type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle';

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className={`fixed bottom-4 right-4 z-[110] px-3 py-2 rounded-lg shadow-lg text-white flex items-center gap-2 text-xs ${bg}`}
      role="status"
    >
      <i className={`fas ${icon} text-[11px]`} aria-hidden="true" />
      {message}
    </motion.div>
  );
};

/* ================================================================== */
/* Confirm dialog                                                      */
/* ================================================================== */

const ConfirmDialog = ({
  open, title, message, confirmLabel = 'Delete', onConfirm, onCancel, busy = false,
}) => {
  useEffect(() => {
    if (!open || busy) return undefined;
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl max-w-xs w-full p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <i className="fas fa-exclamation-triangle text-red-500 text-xs" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-0.5">
              {title}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-xs disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <i className="fas fa-spinner fa-spin text-[10px]" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Generic lightbox                                                    */
/* ================================================================== */

const Lightbox = ({ images, captions = [], initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, next, prev]);

  useEffect(() => {
    if (index >= images.length) setIndex(0);
  }, [images.length, index]);

  if (!images.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[105] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center z-30"
      >
        <i className="fas fa-times text-sm" aria-hidden="true" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-30"
          >
            <i className="fas fa-chevron-left text-sm" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-30"
          >
            <i className="fas fa-chevron-right text-sm" aria-hidden="true" />
          </button>
        </>
      )}

      <div
        className="max-w-full max-h-full w-full h-full flex flex-col items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[index]}
          alt=""
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl select-none"
          draggable={false}
        />
        {captions[index] && (
          <p className="mt-3 text-white bg-black/50 px-3 py-1.5 rounded-md text-xs max-w-lg text-center">
            {captions[index]}
          </p>
        )}
      </div>

      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs rounded-full px-3 py-1 z-30">
          {index + 1} / {images.length}
        </div>
      )}
    </motion.div>
  );
};

/* ================================================================== */
/* Timeline Modal                                                      */
/* ================================================================== */

const TimelineModal = ({ entry, onClose, onSave, onToast }) => {
  const [year, setYear] = useState(entry?.year || '');
  const [title, setTitle] = useState(entry?.title || '');
  const [description, setDescription] = useState(entry?.description || '');
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState(entry?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [removedPhotos, setRemovedPhotos] = useState([]);

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((prev) => [...prev, ...files]);
  };

  const removeExistingPhoto = (url) => {
    setRemovedPhotos((prev) => [...prev, url]);
    setExistingPhotos((prev) => prev.filter((p) => p !== url));
  };

  const removeNewPhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!year || !description.trim()) {
      onToast?.('Year and description are required', 'error');
      return;
    }
    setUploading(true);
    try {
      const newPhotoUrls = [];
      for (const file of photos) {
        const storageRef = ref(
          storage,
          `timelinePhotos/${Date.now()}_${file.name}`,
        );
        await uploadBytesResumable(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newPhotoUrls.push(url);
      }
      for (const url of removedPhotos) {
        try {
          await deleteObject(ref(storage, url));
        } catch { /* ignore */ }
      }
      const finalPhotos = [...existingPhotos, ...newPhotoUrls];
      const data = {
        year: parseInt(year, 10),
        title: title.trim() || `Milestone ${year}`,
        description: description.trim(),
        photos: finalPhotos,
        updatedAt: serverTimestamp(),
      };
      if (entry) {
        await updateDoc(doc(db, 'timeline', entry.id), data);
      } else {
        await addDoc(collection(db, 'timeline'), {
          ...data,
          createdAt: serverTimestamp(),
        });
      }
      onSave?.();
      onClose();
    } catch (err) {
      console.error(err);
      onToast?.('Failed to save timeline entry', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold mb-3 text-gray-800 dark:text-white">
          {entry ? 'Edit Milestone' : 'Add Milestone'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-2.5">
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Year *
            </label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full p-1.5 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-1.5 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Description *
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-1.5 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Photos
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full text-xs"
            />
            {existingPhotos.length > 0 && (
              <div className="mt-2">
                <p className="text-[11px] text-gray-500 mb-1">Current photos:</p>
                <div className="flex flex-wrap gap-1.5">
                  {existingPhotos.map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={url}
                        alt="preview"
                        className="h-14 w-14 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingPhoto(url)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]"
                        aria-label="Remove"
                      >
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {photos.length > 0 && (
              <div className="mt-2">
                <p className="text-[11px] text-gray-500 mb-1">New photos:</p>
                <div className="flex flex-wrap gap-1.5">
                  {photos.map((file, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="h-14 w-14 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewPhoto(idx)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]"
                        aria-label="Remove"
                      >
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-1.5">
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 bg-orange hover:bg-orange-dark text-white py-1.5 rounded text-xs font-medium disabled:opacity-60"
            >
              {uploading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-1.5 rounded text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Team Modal                                                          */
/* ================================================================== */

const TeamModal = ({ item, allMembers, onClose, onSave, onToast, users }) => {
  const [form, setForm] = useState({
    name: item?.name || '',
    role: item?.role || '',
    committee: item?.committee || '',
    bio: item?.bio || '',
    photoUrl: item?.photoUrl || null,
    userId: item?.userId || null,
    linkedIn: item?.linkedIn || '',
    email: item?.email || '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(item?.photoUrl || null);
  const [removeExistingPhoto, setRemoveExistingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [showUserPicker, setShowUserPicker] = useState(false);

  const candidateUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const takenIds = new Set(
      (allMembers || [])
        .filter((m) => m.userId && m.id !== item?.id)
        .map((m) => m.userId),
    );
    const q = userSearch.trim().toLowerCase();
    return users
      .filter((u) => !takenIds.has(u.id))
      .filter((u) => {
        if (!q) return true;
        return (
          (u.displayName || u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.position || '').toLowerCase().includes(q)
        );
      })
      .slice(0, 20);
  }, [users, allMembers, item?.id, userSearch]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onToast?.('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onToast?.('Image must be less than 5MB', 'error');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemoveExistingPhoto(true);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemoveExistingPhoto(true);
  };

  const pickUser = (u) => {
    setForm((f) => ({
      ...f,
      userId: u.id,
      name: u.displayName || u.name || f.name,
      role: f.role || u.position || '',
      email: u.email || f.email,
      photoUrl: u.photoUrl || f.photoUrl,
    }));
    if (u.photoUrl) {
      setPhotoPreview(u.photoUrl);
    }
    setShowUserPicker(false);
    setUserSearch('');
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      onToast?.('Name is required', 'error');
      return;
    }
    if (!form.role.trim()) {
      onToast?.('Role is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      let finalPhotoUrl = form.photoUrl || null;

      if (photoFile) {
        const storageRef = ref(
          storage,
          `teamPhotos/${Date.now()}_${photoFile.name}`,
        );
        await uploadBytes(storageRef, photoFile);
        finalPhotoUrl = await getDownloadURL(storageRef);
      }

      if (removeExistingPhoto && !photoFile && item?.photoUrl) {
        try {
          await deleteObject(ref(storage, item.photoUrl));
        } catch { /* ignore */ }
        finalPhotoUrl = null;
      }

      let order = item?.order;
      if (!item) {
        const maxOrder = (allMembers || []).reduce(
          (max, m) => Math.max(max, Number(m.order) || 0),
          0,
        );
        order = maxOrder + 1;
      }

      const data = {
        name: form.name.trim(),
        role: form.role.trim(),
        committee: form.committee?.trim() || '',
        bio: form.bio?.trim() || '',
        photoUrl: finalPhotoUrl,
        userId: form.userId || null,
        linkedIn: form.linkedIn?.trim() || '',
        email: form.email?.trim() || '',
        order,
        updatedAt: serverTimestamp(),
      };

      if (item) {
        await updateDoc(doc(db, 'team', item.id), data);
      } else {
        await addDoc(collection(db, 'team'), {
          ...data,
          createdAt: serverTimestamp(),
        });
      }

      onSave?.();
      onClose();
    } catch (err) {
      console.error(err);
      onToast?.('Failed to save team member', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold text-gray-800 dark:text-white">
            {item ? 'Edit Team Member' : 'Add Team Member'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <i className="fas fa-times text-sm" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-2.5">
          {users?.length > 0 && (
            <div className="p-2.5 bg-orange/5 dark:bg-orange/10 border border-orange/30 rounded-lg">
              <button
                type="button"
                onClick={() => setShowUserPicker((s) => !s)}
                className="w-full flex items-center justify-between text-xs font-medium text-orange"
              >
                <span className="flex items-center gap-1.5">
                  <i className="fas fa-user-plus text-[10px]" aria-hidden="true" />
                  Import from registered members
                </span>
                <i
                  className={`fas fa-chevron-${showUserPicker ? 'up' : 'down'} text-[10px]`}
                  aria-hidden="true"
                />
              </button>

              {showUserPicker && (
                <div className="mt-2.5 space-y-2">
                  <div className="relative">
                    <i
                      className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]"
                      aria-hidden="true"
                    />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search members by name or email..."
                      className="w-full pl-7 pr-2 py-1 border rounded text-xs dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-orange focus:outline-none"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto border rounded dark:border-gray-700 bg-white dark:bg-gray-800">
                    {candidateUsers.length === 0 ? (
                      <p className="p-2 text-center text-[11px] text-gray-500">
                        No matching members.
                      </p>
                    ) : (
                      candidateUsers.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => pickUser(u)}
                          className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b last:border-b-0 dark:border-gray-700"
                        >
                          <div className="w-7 h-7 rounded-full bg-orange/20 overflow-hidden flex items-center justify-center flex-shrink-0">
                            {u.photoUrl ? (
                              <img
                                src={u.photoUrl}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-[10px] font-bold text-orange">
                                {(u.displayName || u.name || 'U')
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-800 dark:text-white truncate">
                              {u.displayName || u.name || 'Unnamed'}
                            </p>
                            <p className="text-[10px] text-gray-500 truncate">
                              {u.position || u.email}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Photo */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Photo
            </label>
            <div className="flex items-center gap-2.5">
              <div className="w-12 h-12 rounded-full bg-orange/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <i className="fas fa-user-tie text-lg text-orange" aria-hidden="true" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="cursor-pointer text-[11px] bg-orange hover:bg-orange-dark text-white px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1 w-fit">
                  <i className="fas fa-upload text-[10px]" aria-hidden="true" />
                  {photoPreview ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                </label>
                {photoPreview && (
                  <button
                    type="button"
                    onClick={clearPhoto}
                    className="text-[10px] text-red-500 hover:underline w-fit"
                  >
                    Remove photo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Juan Dela Cruz"
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role / Position *
            </label>
            <input
              type="text"
              list="team-role-options"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="e.g., President"
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <datalist id="team-role-options">
              {TEAM_ROLES.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </div>

          {/* Committee */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Committee (optional)
            </label>
            <input
              type="text"
              list="team-committee-options"
              value={form.committee}
              onChange={(e) => setForm({ ...form, committee: e.target.value })}
              placeholder="e.g., Executive Committee"
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <datalist id="team-committee-options">
              {TEAM_COMMITTEES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
              Members with the same committee are grouped together.
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email (optional)
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@example.com"
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* LinkedIn */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              LinkedIn URL (optional)
            </label>
            <input
              type="url"
              value={form.linkedIn}
              onChange={(e) => setForm({ ...form, linkedIn: e.target.value })}
              placeholder="https://linkedin.com/in/username"
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* Bio */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bio (optional)
            </label>
            <textarea
              rows={2}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Brief introduction..."
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
            />
          </div>

          <div className="flex gap-2 pt-1.5">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-orange hover:bg-orange-dark text-white py-1.5 rounded-lg text-xs disabled:opacity-50 flex items-center justify-center gap-1 font-medium"
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin text-[10px]" aria-hidden="true" />
                  Saving...
                </>
              ) : item ? (
                'Save Changes'
              ) : (
                'Add Member'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-1.5 rounded-lg text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Award Modal                                                         */
/* ================================================================== */

const AwardModal = ({ item, onClose, onSave, onToast }) => {
  const [formData, setFormData] = useState({
    year: item?.year || '',
    category: item?.category || 'Chapter',
    level: item?.level || '',
    president: item?.president || '',
    description: item?.description || '',
    imageFile: null,
    imagePreview: item?.imageUrl || null,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onToast?.('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onToast?.('Image size should be less than 5MB', 'error');
      return;
    }
    setFormData((f) => ({
      ...f,
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.year?.trim() || !formData.category) {
      onToast?.('Year and Category are required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      let imageUrl =
        formData.imagePreview && !formData.imageFile ? formData.imagePreview : '';
      if (formData.imageFile) {
        const storageRef = ref(
          storage,
          `awards/${Date.now()}_${formData.imageFile.name}`,
        );
        await uploadBytes(storageRef, formData.imageFile);
        imageUrl = await getDownloadURL(storageRef);
        if (item?.imageUrl && item.imageUrl !== imageUrl) {
          try {
            await deleteObject(ref(storage, item.imageUrl));
          } catch { /* ignore */ }
        }
      }

      const data = {
        year: formData.year.trim(),
        category: formData.category,
        level: formData.level?.trim() || '',
        president: formData.president?.trim() || '',
        description: formData.description?.trim() || '',
        imageUrl,
        updatedAt: serverTimestamp(),
      };

      if (item) {
        await updateDoc(doc(db, 'awards', item.id), data);
      } else {
        await addDoc(collection(db, 'awards'), {
          ...data,
          createdAt: serverTimestamp(),
        });
      }

      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const adminBoardIds = [];
        usersSnap.forEach((ds) => {
          const d = ds.data();
          if (d.isAdmin === true || d.role === 'Board' || d.role === 'board') {
            adminBoardIds.push(ds.id);
          }
        });
        if (adminBoardIds.length > 0) {
          await addDoc(collection(db, 'notifications'), {
            title: item ? '✏️ Award Updated' : '🏆 New Award Added',
            body: `${data.year} ${data.category} Award - ${data.level || 'Recognition'}`,
            recipientType: 'users',
            recipients: adminBoardIds,
            createdAt: serverTimestamp(),
            readBy: [],
          });
        }
      } catch (err) {
        console.warn('award notification failed', err);
      }

      onSave?.();
      onClose();
    } catch (err) {
      console.error(err);
      onToast?.('Failed to save award', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold text-gray-800 dark:text-white">
            {item ? 'Edit Award' : 'Add New Award'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <i className="fas fa-times text-sm" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Year *
            </label>
            <input
              type="text"
              placeholder="e.g., 2025"
              value={formData.year}
              onChange={(e) =>
                setFormData({ ...formData, year: e.target.value })
              }
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              {AWARD_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Level / Recognition
            </label>
            <input
              type="text"
              value={formData.level}
              onChange={(e) =>
                setFormData({ ...formData, level: e.target.value })
              }
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              President Name
            </label>
            <input
              type="text"
              value={formData.president}
              onChange={(e) =>
                setFormData({ ...formData, president: e.target.value })
              }
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              rows={2}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full p-1.5 text-xs border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Award Image
            </label>
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-orange transition"
              onClick={() =>
                document.getElementById('awardImageInput').click()
              }
            >
              <i className="fas fa-cloud-upload-alt text-orange text-lg mb-1" aria-hidden="true" />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Click to upload image
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, GIF (Max 5MB)</p>
              <input
                id="awardImageInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>
            {formData.imagePreview && (
              <div className="mt-2 relative inline-block">
                <img
                  src={formData.imagePreview}
                  className="h-16 rounded object-cover"
                  alt="Preview"
                />
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      imagePreview: null,
                      imageFile: null,
                    })
                  }
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]"
                  aria-label="Remove image"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-orange hover:bg-orange-dark text-white py-1.5 rounded-lg text-xs disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin text-[10px]" aria-hidden="true" />
                  Saving...
                </>
              ) : item ? (
                'Update Award'
              ) : (
                'Add Award'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-1.5 rounded-lg text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Stat Card                                                           */
/* ================================================================== */

const StatCard = ({ value, label, icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 text-center">
    <div className="text-xl font-bold text-orange flex items-center justify-center gap-1">
      {icon && <i className={`fas ${icon} text-sm`} aria-hidden="true" />}
      {value}
    </div>
    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
  </div>
);

/* ================================================================== */
/* Section tabs                                                        */
/* ================================================================== */

const SECTIONS = [
  { id: 'mission', label: 'Mission & Vision', icon: 'fa-bullseye' },
  { id: 'history', label: 'Our History', icon: 'fa-timeline' },
  { id: 'team', label: 'Our Team', icon: 'fa-users' },
  { id: 'awards', label: 'Awards', icon: 'fa-medal' },
];

const SectionTabs = ({ active, onChange }) => (
  <div className="sticky top-16 z-30 -mx-4 px-4 py-1.5 bg-gradient-to-b from-gray-50/95 to-gray-50/80 dark:from-gray-900/95 dark:to-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 mb-6">
    <div className="max-w-6xl mx-auto flex gap-1 overflow-x-auto scrollbar-hide">
      {SECTIONS.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onChange(s.id)}
          aria-current={active === s.id ? 'true' : undefined}
          className={`flex items-center gap-1 whitespace-nowrap text-[11px] md:text-xs px-2.5 md:px-3 py-1 rounded-full transition ${
            active === s.id
              ? 'bg-orange text-white font-medium shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-orange/10 hover:text-orange'
          }`}
        >
          <i className={`fas ${s.icon} text-[9px] md:text-[10px]`} aria-hidden="true" />
          {s.label}
        </button>
      ))}
    </div>
  </div>
);

/* ================================================================== */
/* Team Card                                                           */
/* ================================================================== */

const TeamCard = ({
  member, idx, total, canManage, onEdit, onDelete, onMove,
}) => (
  <motion.div
    variants={fadeInUp}
    whileHover={{ y: -6 }}
    className="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden text-center group relative"
  >
    {canManage && (
      <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
        <button
          type="button"
          onClick={onEdit}
          className="bg-blue-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition shadow-md"
          aria-label="Edit team member"
          title="Edit"
        >
          <i className="fas fa-pen text-[9px]" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition shadow-md"
          aria-label="Delete team member"
          title="Delete"
        >
          <i className="fas fa-trash-alt text-[9px]" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => onMove(member, -1)}
          disabled={idx === 0}
          className="bg-gray-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition shadow-md disabled:opacity-30"
          aria-label="Move up"
          title="Move up"
        >
          <i className="fas fa-arrow-up text-[9px]" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => onMove(member, 1)}
          disabled={idx === total - 1}
          className="bg-gray-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition shadow-md disabled:opacity-30"
          aria-label="Move down"
          title="Move down"
        >
          <i className="fas fa-arrow-down text-[9px]" aria-hidden="true" />
        </button>
      </div>
    )}

    <div className="p-4">
      {member.photoUrl ? (
        <img
          src={member.photoUrl}
          alt={member.name}
          className="w-20 h-20 rounded-full mx-auto object-cover border-2 border-orange/30 group-hover:border-orange transition-all mb-2.5"
        />
      ) : (
        <div className="w-20 h-20 rounded-full mx-auto bg-orange/20 flex items-center justify-center mb-2.5">
          <i className="fas fa-user-tie text-2xl text-orange" aria-hidden="true" />
        </div>
      )}
      <h3 className="text-sm font-bold text-gray-800 dark:text-white">
        {member.name}
      </h3>
      <p className="text-orange font-medium mt-0.5 text-xs">{member.role}</p>
      {member.committee && (
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center justify-center gap-1">
          <i className="fas fa-sitemap text-[9px]" aria-hidden="true" />
          {member.committee}
        </p>
      )}
      {member.bio && (
        <p className="text-gray-600 dark:text-gray-300 text-xs mt-2 line-clamp-3">
          {member.bio}
        </p>
      )}
      {(member.email || member.linkedIn) && (
        <div className="flex justify-center gap-1.5 mt-2">
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-orange hover:text-white text-gray-600 dark:text-gray-300 flex items-center justify-center transition"
              aria-label={`Email ${member.name}`}
              title={member.email}
            >
              <i className="fas fa-envelope text-[10px]" aria-hidden="true" />
            </a>
          )}
          {member.linkedIn && (
            <a
              href={member.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-[#0A66C2] hover:text-white text-gray-600 dark:text-gray-300 flex items-center justify-center transition"
              aria-label={`LinkedIn profile of ${member.name}`}
            >
              <i className="fab fa-linkedin-in text-[10px]" aria-hidden="true" />
            </a>
          )}
        </div>
      )}
    </div>
  </motion.div>
);

/* ================================================================== */
/* About Page                                                          */
/* ================================================================== */

const About = () => {
  const { user, isAdmin, isBoard } = useAuth();
  const canManage = isAdmin || isBoard;

  /* -------------------- State -------------------- */
  const [mission, setMission] = useState('');
  const [vision, setVision] = useState('');
  const [timelineEntries, setTimelineEntries] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [awards, setAwards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeSection, setActiveSection] = useState('mission');
  const [toast, setToast] = useState(null);

  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(false);

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const [showAwardModal, setShowAwardModal] = useState(false);
  const [editingAward, setEditingAward] = useState(null);
  const [awardToDelete, setAwardToDelete] = useState(null);
  const [filterYear, setFilterYear] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearch = useDebounced(searchTerm, 200);

  const [lightbox, setLightbox] = useState(null);

  const showToast = useCallback(
    (message, type = 'success') => setToast({ message, type }),
    [],
  );

  const fetchTimeline = useCallback(async () => {
    const snap = await getDocs(collection(db, 'timeline'));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0));
    setTimelineEntries(list);
  }, []);

  const fetchAwards = useCallback(async () => {
    const snap = await getDocs(collection(db, 'awards'));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0));
    setAwards(list);
  }, []);

  const fetchTeam = useCallback(async () => {
    const q = query(collection(db, 'team'), orderBy('order', 'asc'));
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => {
      const ao = Number(a.order) || 0;
      const bo = Number(b.order) || 0;
      if (ao !== bo) return ao - bo;
      return (a.name || '').localeCompare(b.name || '');
    });
    setTeamMembers(list);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [settingsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'siteSettings')),
          fetchTimeline(),
          fetchTeam(),
          fetchAwards(),
        ]);
        if (!alive) return;

        if (settingsSnap.exists()) {
          const d = settingsSnap.data();
          setMission(d.mission || '');
          setVision(d.vision || '');
        }
      } catch (err) {
        console.error('About fetch failed:', err);
        if (alive) showToast('Failed to load page data', 'error');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchTimeline, fetchTeam, fetchAwards, showToast]);

  useEffect(() => {
    if (!canManage) return undefined;
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        if (!alive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const approved = list.filter(
          (u) =>
            u.isApproved === true ||
            u.approved === true ||
            u.registrationStatus === 'approved',
        );
        setUsers(approved);
      } catch (err) {
        console.warn('Failed to load users for team import:', err);
      }
    })();
    return () => { alive = false; };
  }, [canManage]);

  const missionRef = useRef(null);
  const historyRef = useRef(null);
  const teamRef = useRef(null);
  const awardsRef = useRef(null);

  const sectionRefs = useMemo(
    () => ({
      mission: missionRef,
      history: historyRef,
      team: teamRef,
      awards: awardsRef,
    }),
    [],
  );

  const scrollToSection = useCallback(
    (id) => {
      setActiveSection(id);
      const el = sectionRefs[id]?.current;
      if (el) {
        const y =
          el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    },
    [sectionRefs],
  );

  useEffect(() => {
    const onScroll = () => {
      let active = 'mission';
      for (const id of SECTION_ORDER) {
        const el = sectionRefs[id]?.current;
        if (el && el.getBoundingClientRect().top <= 180) active = id;
      }
      setActiveSection(active);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sectionRefs]);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash || !sectionRefs[hash]) return;
    const t = setTimeout(() => scrollToSection(hash), 120);
    return () => clearTimeout(t);
  }, [sectionRefs, scrollToSection]);

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    setDeletingEntry(true);
    try {
      if (entryToDelete.photos?.length) {
        for (const url of entryToDelete.photos) {
          try {
            await deleteObject(ref(storage, url));
          } catch { /* ignore */ }
        }
      }
      await deleteDoc(doc(db, 'timeline', entryToDelete.id));
      await fetchTimeline();
      showToast('Milestone deleted');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete milestone', 'error');
    } finally {
      setDeletingEntry(false);
      setEntryToDelete(null);
    }
  };

  const openTimelineLightbox = (photos, idx) => {
    setLightbox({ images: photos, captions: [], initialIndex: idx });
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    try {
      if (memberToDelete.photoUrl) {
        try {
          await deleteObject(ref(storage, memberToDelete.photoUrl));
        } catch { /* ignore */ }
      }
      await deleteDoc(doc(db, 'team', memberToDelete.id));
      await fetchTeam();
      showToast('Team member removed');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete team member', 'error');
    } finally {
      setMemberToDelete(null);
    }
  };

  const handleMoveMember = async (member, direction) => {
    const sorted = [...teamMembers].sort((a, b) => {
      const ao = Number(a.order) || 0;
      const bo = Number(b.order) || 0;
      if (ao !== bo) return ao - bo;
      return (a.name || '').localeCompare(b.name || '');
    });
    const idx = sorted.findIndex((m) => m.id === member.id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return;

    const a = sorted[idx];
    const b = sorted[swapIdx];

    const aOrder = Number.isFinite(Number(a.order)) ? Number(a.order) : idx + 1;
    const bOrder = Number.isFinite(Number(b.order)) ? Number(b.order) : swapIdx + 1;

    try {
      setTeamMembers((prev) =>
        prev
          .map((m) =>
            m.id === a.id
              ? { ...m, order: bOrder }
              : m.id === b.id
              ? { ...m, order: aOrder }
              : m,
          )
          .sort((x, y) => (Number(x.order) || 0) - (Number(y.order) || 0)),
      );

      await Promise.all([
        updateDoc(doc(db, 'team', a.id), { order: bOrder, updatedAt: serverTimestamp() }),
        updateDoc(doc(db, 'team', b.id), { order: aOrder, updatedAt: serverTimestamp() }),
      ]);
    } catch (err) {
      console.error('Reorder failed:', err);
      showToast('Failed to reorder team', 'error');
      await fetchTeam();
    }
  };

  const handleDeleteAward = async () => {
    if (!awardToDelete) return;
    try {
      if (awardToDelete.imageUrl) {
        try {
          await deleteObject(ref(storage, awardToDelete.imageUrl));
        } catch { /* ignore */ }
      }
      await deleteDoc(doc(db, 'awards', awardToDelete.id));
      await fetchAwards();
      showToast('Award deleted');
    } catch (err) {
      console.error(err);
      showToast('Delete failed', 'error');
    } finally {
      setAwardToDelete(null);
    }
  };

  const uniqueYears = useMemo(
    () =>
      [...new Set(awards.map((a) => a.year).filter(Boolean))].sort(
        (a, b) => Number(b) - Number(a),
      ),
    [awards],
  );

  const filteredAwards = useMemo(() => {
    let f = [...awards];
    if (filterYear) f = f.filter((a) => a.year === filterYear);
    if (filterCategory) f = f.filter((a) => a.category === filterCategory);
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      f = f.filter(
        (a) =>
          a.year?.toLowerCase().includes(term) ||
          a.level?.toLowerCase().includes(term) ||
          a.president?.toLowerCase().includes(term) ||
          a.description?.toLowerCase().includes(term) ||
          a.category?.toLowerCase().includes(term),
      );
    }
    return f;
  }, [awards, filterYear, filterCategory, debouncedSearch]);

  const awardLightboxImages = useMemo(
    () => filteredAwards.map((a) => a.imageUrl).filter(Boolean),
    [filteredAwards],
  );

  const awardLightboxCaptions = useMemo(
    () =>
      filteredAwards.map(
        (a) =>
          `${a.year} ${a.category} Award - ${a.level || 'Recognition'}${
            a.president ? ` | President: ${a.president}` : ''
          }`,
      ),
    [filteredAwards],
  );

  const stats = useMemo(
    () => ({
      totalAwards: filteredAwards.length,
      years: uniqueYears.length,
      categories: AWARD_CATEGORIES.filter((c) =>
        filteredAwards.some((a) => a.category === c),
      ).length,
    }),
    [filteredAwards, uniqueYears],
  );

  const exportAwardsCSV = () => {
    if (!filteredAwards.length) {
      showToast('No awards to export', 'error');
      return;
    }
    const headers = ['Year', 'Category', 'Level', 'President', 'Description', 'Image URL'];
    const rows = filteredAwards.map((a) => [
      a.year,
      a.category || '',
      a.level || '',
      a.president || '',
      a.description || '',
      a.imageUrl || '',
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','),
      )
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `awards_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    showToast('Exported to CSV');
  };

  const teamGrouped = useMemo(() => {
    if (!teamMembers.length) return null;

    const hasCommittees = teamMembers.some(
      (m) => m.committee && m.committee.trim(),
    );
    if (!hasCommittees) return null;

    const groups = new Map();
    teamMembers.forEach((m) => {
      const key = (m.committee && m.committee.trim()) || 'General Members';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    });

    const sortedKeys = [...groups.keys()].sort((a, b) => {
      if (a === 'General Members') return 1;
      if (b === 'General Members') return -1;
      const ai = TEAM_COMMITTEES.indexOf(a);
      const bi = TEAM_COMMITTEES.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });

    return sortedKeys.map((committee) => ({
      committee,
      members: groups.get(committee) || [],
    }));
  }, [teamMembers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pb-12">
        <div className="max-w-6xl mx-auto px-4 pt-20">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, type: 'spring', stiffness: 100 }}
            className="text-center mb-8"
          >
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 dark:text-white relative inline-block">
              About Us
              <span className="absolute -bottom-1.5 left-0 w-1/2 h-0.5 bg-orange rounded-full" />
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-3 text-sm max-w-xl mx-auto">
              Our mission, our history, our people, and our achievements.
            </p>
          </motion.div>

          {/* Tabs */}
          <SectionTabs active={activeSection} onChange={scrollToSection} />
        </div>

        <div className="max-w-6xl mx-auto px-4">
          {/* ============ MISSION & VISION ============ */}
          <section
            id="mission"
            ref={sectionRefs.mission}
            className="scroll-mt-28 mb-12"
          >
            {mission || vision ? (
              <div className="grid md:grid-cols-2 gap-5">
                {mission && (
                  <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ y: -4 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md hover:shadow-xl transition-all"
                  >
                    <div className="text-orange mb-2.5">
                      <i className="fas fa-bullseye text-2xl" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                      Our Mission
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      {mission}
                    </p>
                  </motion.div>
                )}
                {vision && (
                  <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.5, delay: 0.08 }}
                    whileHover={{ y: -4 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md hover:shadow-xl transition-all"
                  >
                    <div className="text-orange mb-2.5">
                      <i className="fas fa-eye text-2xl" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                      Our Vision
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      {vision}
                    </p>
                  </motion.div>
                )}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8 text-sm">
                Mission and vision coming soon.
              </p>
            )}
          </section>

          {/* ============ HISTORY ============ */}
          <section
            id="history"
            ref={sectionRefs.history}
            className="scroll-mt-28 mb-12"
          >
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white relative inline-block">
                Our History
                <span className="absolute -bottom-1.5 left-0 w-1/2 h-0.5 bg-orange rounded-full" />
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mt-2 text-xs max-w-xl mx-auto">
                Newest milestones first
              </p>
            </div>

            {timelineEntries.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow">
                <i className="fas fa-timeline text-3xl text-gray-400 mb-3" aria-hidden="true" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No history entries yet.
                </p>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEntry(null);
                      setShowTimelineModal(true);
                    }}
                    className="mt-4 bg-orange hover:bg-orange-dark text-white font-semibold px-4 py-2 rounded-full transition inline-flex items-center gap-1.5 text-xs"
                  >
                    <i className="fas fa-plus text-[10px]" aria-hidden="true" /> Add First Milestone
                  </button>
                )}
              </div>
            ) : (
              <>
                {canManage && (
                  <div className="flex justify-end mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingEntry(null);
                        setShowTimelineModal(true);
                      }}
                      className="bg-orange hover:bg-orange-dark text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition text-xs"
                    >
                      <i className="fas fa-plus text-[10px]" aria-hidden="true" /> Add Milestone
                    </button>
                  </div>
                )}

                <div className="relative">
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 bg-gradient-to-b from-orange via-orange/40 to-transparent h-full rounded-full hidden md:block" />
                  <div className="space-y-10">
                    {timelineEntries.map((entry, idx) => (
                      <motion.div
                        key={entry.id}
                        custom={idx}
                        variants={timelineItemVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-60px' }}
                        className="relative"
                      >
                        <div className="absolute left-1/2 transform -translate-x-1/2 -top-4 z-10 hidden md:flex">
                          <div className="bg-gradient-to-r from-orange to-orange-dark text-white font-bold text-sm px-4 py-1 rounded-full shadow-lg border-2 border-white dark:border-gray-800">
                            {entry.year}
                          </div>
                        </div>
                        <div className="md:hidden flex justify-center mb-3">
                          <div className="bg-gradient-to-r from-orange to-orange-dark text-white font-bold text-sm px-3.5 py-1 rounded-full shadow-lg">
                            {entry.year}
                          </div>
                        </div>

                        <div
                          className={`flex flex-col md:flex-row ${
                            idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                          } items-start gap-5 md:gap-8`}
                        >
                          <div className="hidden md:block md:w-1/2" />
                          <div className="w-full md:w-1/2">
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
                              {entry.photos?.length > 0 && (
                                <div
                                  className={`grid gap-0.5 p-2 ${
                                    entry.photos.length === 1
                                      ? 'grid-cols-1'
                                      : 'grid-cols-2'
                                  }`}
                                >
                                  {entry.photos.slice(0, 4).map((photo, pIdx) => (
                                    <div
                                      key={pIdx}
                                      className="relative overflow-hidden rounded-md cursor-pointer group"
                                      onClick={() =>
                                        openTimelineLightbox(entry.photos, pIdx)
                                      }
                                    >
                                      <img
                                        src={photo}
                                        alt={`History ${entry.year}`}
                                        className="w-full h-28 object-cover transition-transform duration-700 group-hover:scale-110"
                                      />
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                        <i className="fas fa-search-plus text-white text-base" aria-hidden="true" />
                                      </div>
                                    </div>
                                  ))}
                                  {entry.photos.length > 4 && (
                                    <div
                                      className="flex items-center justify-center bg-gray-800/50 rounded-md h-28 cursor-pointer hover:bg-gray-800/70 transition"
                                      onClick={() =>
                                        openTimelineLightbox(entry.photos, 4)
                                      }
                                    >
                                      <span className="text-white text-sm font-bold">
                                        +{entry.photos.length - 4} more
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="p-4">
                                <h3 className="text-base font-bold text-gray-800 dark:text-white mb-2">
                                  {entry.title || `Milestone ${entry.year}`}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed whitespace-pre-line">
                                  {entry.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {canManage && (
                          <div className="absolute top-0 right-1 md:top-2 md:right-2 flex gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingEntry(entry);
                                setShowTimelineModal(true);
                              }}
                              className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 transition shadow-md"
                              aria-label="Edit milestone"
                            >
                              <i className="fas fa-edit text-[9px]" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEntryToDelete(entry)}
                              className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 transition shadow-md"
                              aria-label="Delete milestone"
                            >
                              <i className="fas fa-trash text-[9px]" aria-hidden="true" />
                            </button>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============ TEAM ============ */}
          <section
            id="team"
            ref={sectionRefs.team}
            className="scroll-mt-28 mb-12"
          >
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white relative inline-block">
                Our Team
                <span className="absolute -bottom-1.5 left-0 w-1/2 h-0.5 bg-orange rounded-full" />
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mt-2 text-xs max-w-xl mx-auto">
                Meet the dedicated individuals leading our chapter
              </p>
            </div>

            {canManage && (
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingMember(null);
                    setShowTeamModal(true);
                  }}
                  className="bg-orange hover:bg-orange-dark text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition text-xs"
                >
                  <i className="fas fa-user-plus text-[10px]" aria-hidden="true" /> Add Team Member
                </button>
              </div>
            )}

            {teamMembers.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow">
                <i className="fas fa-users text-3xl text-gray-400 mb-3" aria-hidden="true" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No team members yet.
                </p>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMember(null);
                      setShowTeamModal(true);
                    }}
                    className="mt-4 bg-orange hover:bg-orange-dark text-white font-semibold px-4 py-2 rounded-full transition inline-flex items-center gap-1.5 text-xs"
                  >
                    <i className="fas fa-user-plus text-[10px]" aria-hidden="true" /> Add First Member
                  </button>
                )}
              </div>
            ) : teamGrouped ? (
              <div className="space-y-8">
                {teamGrouped.map(({ committee, members }) => (
                  <div key={committee}>
                    <div className="flex items-center gap-2.5 mb-4">
                      <h3 className="text-sm md:text-base font-bold text-gray-800 dark:text-white">
                        {committee}
                      </h3>
                      <span className="text-[10px] font-medium bg-orange/10 text-orange px-2 py-0.5 rounded-full">
                        {members.length}
                      </span>
                      <div className="flex-1 h-px bg-gradient-to-r from-orange/40 to-transparent" />
                    </div>

                    <motion.div
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                      variants={staggerContainer}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: '-40px' }}
                    >
                      {members.map((member, idx) => (
                        <TeamCard
                          key={member.id}
                          member={member}
                          idx={idx}
                          total={members.length}
                          canManage={canManage}
                          onEdit={() => {
                            setEditingMember(member);
                            setShowTeamModal(true);
                          }}
                          onDelete={() => setMemberToDelete(member)}
                          onMove={handleMoveMember}
                        />
                      ))}
                    </motion.div>
                  </div>
                ))}
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
              >
                {teamMembers.map((member, idx) => (
                  <TeamCard
                    key={member.id}
                    member={member}
                    idx={idx}
                    total={teamMembers.length}
                    canManage={canManage}
                    onEdit={() => {
                      setEditingMember(member);
                      setShowTeamModal(true);
                    }}
                    onDelete={() => setMemberToDelete(member)}
                    onMove={handleMoveMember}
                  />
                ))}
              </motion.div>
            )}
          </section>

          {/* ============ AWARDS ============ */}
          <section
            id="awards"
            ref={sectionRefs.awards}
            className="scroll-mt-28 mb-12"
          >
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white relative inline-block">
                Awards &amp; Recognition
                <span className="absolute -bottom-1.5 left-0 w-1/2 h-0.5 bg-orange rounded-full" />
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mt-2 text-xs max-w-xl mx-auto">
                Celebrating excellence and outstanding achievements of IIEE-ERCSA
              </p>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
            >
              <StatCard value={stats.totalAwards} label="Total Awards" icon="fa-award" />
              <StatCard value={stats.years} label="Years of Excellence" icon="fa-calendar" />
              <StatCard value={stats.categories} label="Categories" icon="fa-layer-group" />
              <StatCard value="🏆" label="Excellence" icon={null} />
            </motion.div>

            {/* Header row */}
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
              <h3 className="text-sm font-bold text-gray-800 dark:text-white">
                Most Outstanding Overseas Chapter Excellence Awards
              </h3>
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingAward(null);
                    setShowAwardModal(true);
                  }}
                  className="bg-orange hover:bg-orange-dark text-white px-3.5 py-1.5 rounded-full text-xs font-semibold transition shadow-md flex items-center gap-1.5"
                >
                  <i className="fas fa-plus text-[10px]" aria-hidden="true" /> Add Award
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 mb-5">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
                className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300 font-medium text-xs"
              >
                <i
                  className={`fas fa-filter text-[10px] ${showFilters ? 'text-orange' : ''}`}
                  aria-hidden="true"
                />
                Filters {showFilters ? '▲' : '▼'}
              </button>

              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-wrap gap-2 items-center pt-3">
                      <div className="flex-1 min-w-[160px] relative">
                        <i
                          className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]"
                          aria-hidden="true"
                        />
                        <input
                          type="text"
                          placeholder="Search awards..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-7 pr-2 py-1.5 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white text-xs"
                        />
                      </div>
                      <select
                        value={filterYear}
                        onChange={(e) => setFilterYear(e.target.value)}
                        className="p-1.5 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white text-xs"
                      >
                        <option value="">All Years</option>
                        {uniqueYears.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="p-1.5 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white text-xs"
                      >
                        <option value="">All Categories</option>
                        {AWARD_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      {(filterYear || filterCategory || searchTerm) && (
                        <button
                          type="button"
                          onClick={() => {
                            setFilterYear('');
                            setFilterCategory('');
                            setSearchTerm('');
                          }}
                          className="text-orange text-xs hover:underline"
                        >
                          <i className="fas fa-times mr-0.5 text-[10px]" aria-hidden="true" /> Clear
                        </button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          onClick={exportAwardsCSV}
                          className="bg-green-500 hover:bg-green-600 text-white px-2.5 py-1.5 rounded-md text-xs ml-auto"
                        >
                          <i className="fas fa-download mr-1 text-[10px]" aria-hidden="true" /> Export CSV
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Awards grid */}
            {filteredAwards.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow">
                <i className="fas fa-trophy text-3xl text-gray-300 mb-2" aria-hidden="true" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {searchTerm || filterYear || filterCategory
                    ? 'No awards match your filters.'
                    : canManage
                    ? 'No awards yet. Add your first one!'
                    : 'No awards to display yet.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredAwards.map((award, idx) => (
                  <motion.div
                    key={award.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-20px' }}
                    transition={{ delay: Math.min(idx * 0.03, 0.2) }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg transition-all overflow-hidden group relative"
                  >
                    {canManage && (
                      <div className="absolute top-1.5 right-1.5 flex gap-1 z-10">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingAward(award);
                            setShowAwardModal(true);
                          }}
                          className="bg-blue-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                          aria-label="Edit award"
                        >
                          <i className="fas fa-edit text-[9px]" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setAwardToDelete(award)}
                          className="bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                          aria-label="Delete award"
                        >
                          <i className="fas fa-trash-alt text-[9px]" aria-hidden="true" />
                        </button>
                      </div>
                    )}

                    <div
                      className="relative overflow-hidden cursor-pointer"
                      onClick={() => {
                        const imgIdx = awardLightboxImages.indexOf(
                          award.imageUrl,
                        );
                        if (imgIdx >= 0) {
                          setLightbox({
                            images: awardLightboxImages,
                            captions: awardLightboxCaptions,
                            initialIndex: imgIdx,
                          });
                        }
                      }}
                    >
                      <img
                        src={
                          award.imageUrl ||
                          'https://placehold.co/400x300/f0f0f0/orange?text=Award+Image'
                        }
                        alt={`${award.year} ${award.category} Award`}
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.src =
                            'https://placehold.co/400x300/f0f0f0/orange?text=Award+Image';
                        }}
                      />
                      <div className="absolute top-1.5 left-1.5 bg-orange/90 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        {award.year}
                      </div>
                    </div>

                    <div className="p-3 text-center">
                      <div className="inline-block px-2 py-0.5 bg-orange/20 text-orange rounded-full text-[10px] font-semibold mb-1.5">
                        {award.category}
                      </div>
                      {award.level && (
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-0.5">
                          <i className="fas fa-award text-orange mr-1 text-[10px]" aria-hidden="true" />
                          {award.level}
                        </div>
                      )}
                      {award.president && (
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1.5">
                          <i className="fas fa-user-tie mr-1 text-[9px]" aria-hidden="true" />
                          President: {award.president}
                        </div>
                      )}
                      {award.description && (
                        <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2">
                          {award.description}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ============ MODALS ============ */}
      <AnimatePresence>
        {showTimelineModal && (
          <TimelineModal
            entry={editingEntry}
            onClose={() => setShowTimelineModal(false)}
            onSave={fetchTimeline}
            onToast={showToast}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTeamModal && (
          <TeamModal
            item={editingMember}
            allMembers={teamMembers}
            users={users}
            onClose={() => setShowTeamModal(false)}
            onSave={fetchTeam}
            onToast={showToast}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAwardModal && (
          <AwardModal
            item={editingAward}
            onClose={() => setShowAwardModal(false)}
            onSave={fetchAwards}
            onToast={showToast}
          />
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!entryToDelete}
        title="Delete this milestone?"
        message={`"${entryToDelete?.title || `Milestone ${entryToDelete?.year || ''}`}" will be permanently removed along with its photos.`}
        confirmLabel="Delete"
        busy={deletingEntry}
        onConfirm={handleDeleteEntry}
        onCancel={() => !deletingEntry && setEntryToDelete(null)}
      />

      <ConfirmDialog
        open={!!memberToDelete}
        title="Remove team member?"
        message={`"${memberToDelete?.name}" will be removed from the team list.`}
        confirmLabel="Remove"
        onConfirm={handleDeleteMember}
        onCancel={() => setMemberToDelete(null)}
      />

      <ConfirmDialog
        open={!!awardToDelete}
        title="Delete this award?"
        message={`"${awardToDelete?.year} ${awardToDelete?.category}" will be permanently removed.`}
        onConfirm={handleDeleteAward}
        onCancel={() => setAwardToDelete(null)}
      />

      {/* ============ LIGHTBOX ============ */}
      <AnimatePresence>
        {lightbox && (
          <Lightbox
            images={lightbox.images}
            captions={lightbox.captions}
            initialIndex={lightbox.initialIndex}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default About;